import * as React from "react";
import { observer } from "mobx-react";
import { Form } from "antd";
import TaskTypesVisualStore from "../stores/TaskTypesVisualStore";
import { useForm } from "antd/lib/form/Form";
import TaskTypeDialogForm, { periodOptions } from "./TaskTypeDialogForm";
import AppModal from "../../common/components/AppModal";

type Props = {
    onCreated?: (taskTypeId: string) => void | Promise<void>;
    statusFlowMode?: TaskTypesVisualStore["taskTypes"][number]["statusFlowMode"];
    store: TaskTypesVisualStore;
};

export { periodOptions };

const TaskTypeCreateDialog: React.FC<Props> = ({
    onCreated,
    statusFlowMode = "standard",
    store,
}) => {
    const [form] = useForm();

    const handleSubmit = async () => {
        form.validateFields()
            .then(async (values) => {
                const dueDate =
                    values.count && values.period
                        ? { count: values.count, period: values.period }
                        : null;
                const reminderDays =
                    dueDate && values.reminderDays ? values.reminderDays : null;
                const createdTaskTypeId = await store.createTaskType(
                    values.name,
                    values.asignee,
                    values.code,
                    values.canChangeStatusManually,
                    dueDate,
                    reminderDays,
                    statusFlowMode,
                );
                if (createdTaskTypeId) {
                    await onCreated?.(createdTaskTypeId);
                }
                form.resetFields();
            })
            .catch((err) => {
                console.log(err);
            });
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const taskTypeNamesValidator = (rule: any, value: any, callback: any) => {
        const taskTypesToValidate = store!.taskTypes.map((t) =>
            t.name.toLowerCase(),
        );
        if (value && taskTypesToValidate.includes(value.toLowerCase())) {
            callback("Task type with the same name already exists");
        } else {
            callback();
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const taskTypesCodesValidator = (rule: any, value: any, callback: any) => {
        const taskTypesToValidate = store!.taskTypes.map(
            (t) => t.code?.toLowerCase() ?? "",
        );
        if (value && taskTypesToValidate.includes(value.toLowerCase())) {
            callback("Task type with the same code already exists");
        } else {
            callback();
        }
    };

    return (
        <AppModal
            data-id-type="modal-window"
            data-id-name="Create new task type"
            className="alpha-modal task-type-create-modal"
            title="Create task type"
            infoBanner={{
                title: "Task types are optional categories.",
                description:
                    "Use them when tasks need different defaults or workflow settings.",
            }}
            open={store.newTypeDialogVisible}
            closable={false}
            centered
            width={520}
            onCancel={() => {
                store.setNewTypeDialogVisible(false);
                form.resetFields();
            }}
            cancelButtonTitle="Cancel"
            onCancelClick={() => {
                store.setNewTypeDialogVisible(false);
                form.resetFields();
            }}
            cancelButtonProps={{
                "data-id": "button-new-task-type-cancel",
            }}
            submitButtonTitle="Create"
            onSubmitClick={handleSubmit}
            submitButtonProps={{
                "data-id": "button-new-task-type-Save",
                className: "dark",
            }}
        >
            <Form
                layout="vertical"
                data-id="create-task-type-dialog-form"
                form={form}
                className="alpha-form"
                initialValues={{
                    canChangeStatusManually: true,
                    period: "D",
                }}
            >
                <TaskTypeDialogForm
                    form={form}
                    assigneeFieldName="asignee"
                    assigneeUsers={store.usersForProject.filter(
                        (u) => !u.isDeleted,
                    )}
                    shouldAutoGenerateCode
                    nameRules={[
                        {
                            required: true,
                            message: "Name is required",
                            whitespace: true,
                        },
                        { validator: taskTypeNamesValidator },
                    ]}
                    codeRules={[{ validator: taskTypesCodesValidator }]}
                />
            </Form>
        </AppModal>
    );
};

export default observer(TaskTypeCreateDialog);
